import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router";
import { Disclosure, DisclosureButton } from "@headlessui/react";
import Constant from "../../../../utils/constant";
import SelectPageSize from "../../../shared/common/pageSizeDropdown";
import Pagination from "../../../shared/common/pagination";
import Loader from "../../../shared/loader/loader";
import {
  EditIcon,
  ApproveIcon,
  Disapprove,
  CaretUpSolidIcon,
  CaretDownSolidIcon,
  CollapseDownIcon,
  CollapseUpIcon,
  FilterIcon,
  ResetIcon,
} from "../../../../assets/icons/index";
import {
  getAllUsers,
  getAuthUserPermissionsByuserId,
} from "../../../../api/userPermission.service";
import {
  IUserGroupPermission,
  IUserPermission,
  IUserPermissionUsers,
  IUsersSearchRequest,
} from "../../../../model/interface/userPermission";
import AllowedTo from "../../../shared/common/rbac";
import pagePermissions from "../../../../utils/permissions";
import AccessDenied from "../../../shared/common/accessDenied";

const UserPermission: React.FC = () => {
  const defaultPageNumber = 1;
  const defaultPageSize = 10;
  const alphaNumericExpression = new RegExp(/^[0-9^]*$/);
  const defaultFilter: IUsersSearchRequest = {
    username: "",
    schoolName: "",
    userId: 0,
  };
  const [pageNumber, setPageNumber] = useState(defaultPageNumber);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [orderByColumn, setOrderByColumn] = useState("name");
  const [totalPages, setTotalPages] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [sortByDesc, setSortByDesc] = useState<boolean>(true);
  const [toggle, setToggle] = useState<number>();
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [users, setUsers] = useState<IUserPermissionUsers[]>([]);
  const [userPermissions, setUserPermissions] = useState<IUserPermission[]>([]);
  const [filter, setFilter] = useState<IUsersSearchRequest>(defaultFilter);
  const [username, setUsername] = useState("");
  const [userId, setUserId] = useState("");
  const [schoolName, setSchoolName] = useState("");
  const navigate = useNavigate();
  const paginationRef: any = useRef(undefined);
  const pageSizeRef: any = useRef(undefined);

  const handleRedirect = (userId: number) => {
    navigate(Constant.EDIT_USER_PERMISSION.replace(":id", userId.toString()));
  };

  const handleSortBy = (column: string) => {
    setSortByDesc(!sortByDesc);
    setOrderByColumn(column);
  };

  const handleUsernameChange = (value: string) => {
    filter.username = value;
    setFilter(filter);
    setUsername(value);
  };
  const handleUserIdChange = (value: string) => {
    if (value === "") {
      filter.userId = 0;
    } else {
      filter.userId = parseInt(value);
    }
    setFilter(filter);
    setUserId(value);
  };

  const handleSchoolnameChange = (value: string) => {
    filter.schoolName = value;
    setFilter(filter);
    setSchoolName(value);
  };

  async function getUserPermissionUsersData(isReset?, column?) {
    setLoading(true);

    getAllUsers(
      filter,
      orderByColumn,
      sortByDesc ? "ASC" : "DESC",
      pageSize,
      pageNumber
    ).then((response) => {
      setUsers(response.data.data);
      setTotalPages(
        Math.ceil(
          response.data.totalRecords / (isReset ? defaultPageSize : pageSize)
        )
      );
      setTotalRecords(response.data.totalRecords);
      setLoading(false);
      setIsLoaded(true);
    });
  }

  useEffect(() => {
    if (isLoaded) {
      getUserPermissionUsersData();
    }
    setToggle(undefined);
  }, [pageNumber]);

  useEffect(() => {
    if (pageNumber === 1 && isLoaded) {
      getUserPermissionUsersData();

      setIsLoaded(true);
    } else {
      paginationRef?.current?.onReset();
    }
    setToggle(undefined);
  }, [pageSize]);

  useEffect(() => {
    if (isLoaded) {
      getUserPermissionUsersData();
    }
  }, [sortByDesc]);

  const handleUserPermissions = async (userId, index?, type?) => {
    setLoading(true);

    if (type === "show") {
      const response = getAuthUserPermissionsByuserId(userId);
      const data = (await response).data;
      setUserPermissions(data);

      setToggle(index);
    } else {
      setToggle(undefined);
    }
    setLoading(false);
  };

  const handleReset = () => {
    filter.username = "";
    filter.schoolName = "";
    setFilter(filter);
    setUsername("");
    setSchoolName("");
  };

  return (
    <AllowedTo
      perform={pagePermissions.user_permissions_view}
      no={() => <AccessDenied />}
    >
      {loading && <Loader />}

      <div className="flex-1 overflow-auto">
        <div className=" h-auto min-h-half w-full rounded-bl-3xl bg-site-teal px-8  py-8 pt-0 pb-14 mt-7 ">
          <div className="relative  py-1">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-light text-gray-900">
                User Permissions
              </h3>
            </div>
            <div className="pt-6 border-b pb-6">
              <div className="grid grid-cols-12 gap-5">
                <div className="col-span-3">
                  <label className="text-sm text-gray-700">UserId</label>
                  <div className="contentArea">
                    <input
                      id="UserId"
                      name="UserId"
                      type="text"
                      autoComplete="off"
                      className="block mt-1 w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                      onChange={(e) => {
                        if (
                          alphaNumericExpression.test(e.target.value.toString())
                        ) {
                          handleUserIdChange(e.target.value);
                        }
                      }}
                      value={userId}
                    />
                  </div>
                </div>
                <div className="col-span-3">
                  <label className="text-sm text-gray-700">Username</label>
                  <div className="contentArea">
                    <input
                      id="username"
                      name="username"
                      type="text"
                      autoComplete="off"
                      className="block mt-1 w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                      onChange={(e) => {
                        handleUsernameChange(e.target.value);
                      }}
                      value={username}
                    />
                  </div>
                </div>

                <div className="col-span-3">
                  <label className="text-sm text-gray-700">School</label>
                  <div className="contentArea">
                    <input
                      id="school"
                      name="school"
                      type="text"
                      autoComplete="off"
                      value={schoolName}
                      className="block mt-1 w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                      onChange={(e) => {
                        handleSchoolnameChange(e.target.value);
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-start mt-8 gap-2 items-center border-t">
                <button
                  className="inline-flex items-center text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg"
                  onClick={() => {
                    getUserPermissionUsersData();
                  }}
                >
                  <FilterIcon className="w-3 h-3"></FilterIcon>
                  <span>Filter</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleReset()}
                  className="inline-flex items-center px-3 py-2 my-3 hover:text-gray-500 border border-gray-300 text-sm leading-4 font-medium rounded-md shadow-sm text-gray-700 bg-white  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color"
                >
                  <ResetIcon className="w-3 h-3"></ResetIcon>
                  <span className="ml-2">Reset Filter</span>
                </button>
              </div>
            </div>
            <div id="dataContainer" className="mt-4">
              <div className="flex flex-col mt-1">
                <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                  <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-primary-color">
                          <tr>
                            <th
                              scope="col"
                              className="px-3 py-2 font-light whitespace-nowrap text-sm text-center text-white dark:text-white tracking-wider"
                            >
                              <span className="flex gap-2">
                                UserId
                                <span className="cursor-pointer">
                                  {sortByDesc && orderByColumn === "userId" ? (
                                    <CaretUpSolidIcon
                                      onClick={() => handleSortBy("userId")}
                                      className="w-5 h-5"
                                    ></CaretUpSolidIcon>
                                  ) : (
                                    <CaretDownSolidIcon
                                      onClick={() => handleSortBy("userId")}
                                      className="w-5 h-5"
                                    ></CaretDownSolidIcon>
                                  )}
                                </span>
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 font-light whitespace-nowrap text-sm text-center text-white dark:text-white tracking-wider"
                            >
                              <span className="flex gap-2">
                                Name
                                <span className="cursor-pointer">
                                  {!sortByDesc &&
                                  orderByColumn === "firstname" ? (
                                    <CaretUpSolidIcon
                                      onClick={() => handleSortBy("firstname")}
                                      className="w-5 h-5"
                                    ></CaretUpSolidIcon>
                                  ) : (
                                    <CaretDownSolidIcon
                                      onClick={() => handleSortBy("firstname")}
                                      className="w-5 h-5"
                                    ></CaretDownSolidIcon>
                                  )}
                                </span>
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 font-light whitespace-nowrap text-sm text-center text-white dark:text-white tracking-wider"
                            >
                              <span className="flex gap-2">
                                Username
                                <span className="cursor-pointer">
                                  {!sortByDesc &&
                                  orderByColumn === "username" ? (
                                    <CaretUpSolidIcon
                                      onClick={() => handleSortBy("username")}
                                      className="w-5 h-5"
                                    ></CaretUpSolidIcon>
                                  ) : (
                                    <CaretDownSolidIcon
                                      onClick={() => handleSortBy("username")}
                                      className="w-5 h-5"
                                    ></CaretDownSolidIcon>
                                  )}
                                </span>
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 font-light whitespace-nowrap text-sm text-center text-white dark:text-white tracking-wider"
                            >
                              <span className="flex gap-2">Role</span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 font-light whitespace-nowrap text-sm text-center text-white dark:text-white tracking-wider"
                            >
                              <span className="flex gap-2">School</span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 w-12 font-light whitespace-nowrap text-sm text-center text-white dark:text-white tracking-wider"
                            >
                              <span className="flex gap-2">Action</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {users.map((user, index) => (
                            <Disclosure key={index}>
                              {({ open }) => (
                                <>
                                  <tr
                                    key={index}
                                    className={
                                      index % 2 === 0
                                        ? "bg-white"
                                        : "bg-gray-100"
                                    }
                                  >
                                    <td className="px-3 py-2 pl-6  text-sm text-left font-normal text-gray-500 relative">
                                      <div className="flex ...">
                                        <div className="mt-2">
                                          <DisclosureButton className="">
                                            {index !== toggle && (
                                              <CollapseDownIcon
                                                key={index}
                                                className={`w-5 h-5 text-gray-500`}
                                                onClick={(e) => {
                                                  handleUserPermissions(
                                                    user.userId,
                                                    index,
                                                    "show"
                                                  );
                                                }}
                                              />
                                            )}

                                            {index === toggle && (
                                              <CollapseUpIcon
                                                key={index}
                                                className={`w-5 h-5 text-gray-500`}
                                                onClick={(e) => {
                                                  handleUserPermissions(
                                                    user.userId,
                                                    index,
                                                    "hide"
                                                  );
                                                }}
                                              />
                                            )}
                                          </DisclosureButton>
                                        </div>
                                        <div className="px-3 py-2">
                                          <Link
                                            to={Constant.EDIT_USER_PERMISSION.replace(
                                              ":id",
                                              user.userId.toString()
                                            )}
                                            className="cursor-pointer font-medium  text-indigo-600 hover:text-indigo-500 hover:underline"
                                          >
                                            {user.userId}
                                          </Link>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-3 py-2 text-sm text-left text-gray-500 font-normal">
                                      {user.lastname + ", " + user.firstname}
                                    </td>
                                    <td className="px-3 py-2 text-sm text-left text-gray-500 font-normal">
                                      {user.username}
                                    </td>
                                    <td className="px-3 py-2 text-sm text-left text-gray-500 font-normal">
                                      {user.roleName}
                                    </td>
                                    <td className="px-3 py-2 text-sm text-left text-gray-500 font-normal">
                                      {user.schoolName}
                                    </td>
                                    <td className="px-3 py-2 flex justify-start text-gray-500">
                                      <div className="px-3">
                                        <AllowedTo
                                          perform={
                                            pagePermissions.user_permissions_modify
                                          }
                                        >
                                          <EditIcon
                                            onClick={() =>
                                              handleRedirect(user.userId)
                                            }
                                            className="w-4 text-xs font-light cursor-pointer hover:text-secondary-color transaction ease-in-out duration-300"
                                          />{" "}
                                        </AllowedTo>
                                      </div>
                                    </td>
                                  </tr>
                                  <tr
                                    className={
                                      index % 2 === 0
                                        ? "bg-white"
                                        : "bg-gray-100"
                                    }
                                  >
                                    <td colSpan={6} className="relative ">
                                      <div
                                        className={`${
                                          index !== toggle ? "hidden" : ""
                                        } pl-12 pr-12 px-4 pb-2 text-sm text-gray-500`}
                                      >
                                        <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                                          <table className="min-w-full">
                                            <thead>
                                              <tr className="bg-gray-100">
                                                <th
                                                  scope="col"
                                                  className="inline-flex items-center gap-2 px-3 py-2 font-semibold text-gray-500 text-xs text-left tracking-wider"
                                                >
                                                  <span className="flex gap-2">
                                                    Permission Name
                                                  </span>
                                                </th>

                                                <th
                                                  scope="col"
                                                  className="px-3 py-2 text-xs text-center text-gray-500 font-semibold tracking-wider"
                                                >
                                                  View
                                                </th>
                                                <th
                                                  scope="col"
                                                  className="px-3 py-2 text-xs text-center text-gray-500 font-semibold tracking-wider"
                                                >
                                                  Create
                                                </th>
                                                <th
                                                  scope="col"
                                                  className="px-3 py-2 text-xs text-center text-gray-500 font-semibold tracking-wider"
                                                >
                                                  Modify
                                                </th>
                                                <th
                                                  scope="col"
                                                  className="px-3 py-2 text-xs text-center text-gray-500 font-semibold tracking-wider"
                                                >
                                                  Delete
                                                </th>
                                                <th
                                                  scope="col"
                                                  className="px-3 py-2 text-xs text-center text-gray-500 font-semibold tracking-wider"
                                                >
                                                  Upgrade Required
                                                </th>
                                              </tr>
                                            </thead>
                                            <tbody className="divide-y">
                                              {userPermissions.map(
                                                (permission, index) => (
                                                  <tr
                                                    key={index}
                                                    className={"bg-white"}
                                                  >
                                                    <td className="px-3 py-2  whitespace-nowrap text-sm text-left font-normal text-gray-500">
                                                      {permission.name}
                                                    </td>
                                                    <td className="px-3 py-2  whitespace-nowrap text-sm text-center font-normal text-gray-500">
                                                      <div className="flex justify-center">
                                                        {permission.view ? (
                                                          <ApproveIcon />
                                                        ) : (
                                                          <Disapprove />
                                                        )}
                                                      </div>
                                                    </td>
                                                    <td className="px-3 py-2  whitespace-nowrap text-sm text-left font-normal text-gray-500">
                                                      <div className="flex justify-center">
                                                        {permission.create ? (
                                                          <ApproveIcon />
                                                        ) : (
                                                          <Disapprove />
                                                        )}
                                                      </div>
                                                    </td>
                                                    <td className="px-3 py-2  whitespace-nowrap text-sm text-left font-normal text-gray-500">
                                                      <div className="flex justify-center">
                                                        {permission.modify ? (
                                                          <ApproveIcon />
                                                        ) : (
                                                          <Disapprove />
                                                        )}
                                                      </div>
                                                    </td>
                                                    <td className="px-3 py-2  whitespace-nowrap text-sm text-left font-normal text-gray-500">
                                                      <div className="flex justify-center">
                                                        {permission.delete ? (
                                                          <ApproveIcon />
                                                        ) : (
                                                          <Disapprove />
                                                        )}
                                                      </div>
                                                    </td>
                                                    <td className="px-3 py-2  whitespace-nowrap text-sm text-left font-normal text-gray-500">
                                                      <div className="flex justify-center">
                                                        {permission.upgrade ? (
                                                          <ApproveIcon />
                                                        ) : (
                                                          <Disapprove />
                                                        )}
                                                      </div>
                                                    </td>
                                                  </tr>
                                                )
                                              )}
                                            </tbody>
                                          </table>
                                        </div>
                                        {userPermissions.length === 0 && (
                                          <p className="p-2">
                                            No Records Found
                                          </p>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                </>
                              )}
                            </Disclosure>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  {users.length !== 0 ? (
                    <>
                      <Pagination
                        pages={totalPages}
                        setPageNumber={setPageNumber}
                        ref={paginationRef}
                      />
                      <span>
                        <span className="text-sm pr-4 ml-auto text-left-700">
                          Showing {pageNumber} to{" "}
                          {totalRecords < pageSize ? totalRecords : pageSize} of{" "}
                          {totalRecords} results
                        </span>

                        <SelectPageSize
                          setPageSize={setPageSize}
                          type={"second"}
                          ref={pageSizeRef}
                        />
                      </span>
                    </>
                  ) : (
                    <p className="p-2">No Records Found</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AllowedTo>
  );
};

export default UserPermission;
